import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, of, tap } from 'rxjs';
import { AuthService } from './auth.service';
import {
  FascicoloCheckPage,
  FascicoloService,
} from './fascicolo.service';

@Injectable({ providedIn: 'root' })
export class FascicoloStateService {
  private authService = inject(AuthService);
  private fascicoloService = inject(FascicoloService);

  private readonly ANON_KEY = 'fascicolo_anon';

  private pagineSubject = new BehaviorSubject<FascicoloCheckPage[]>([]);
  readonly pagine$ = this.pagineSubject.asObservable();

  /**
   * Falso mentre è in corso il recupero dal server: senza questa informazione le
   * viste mostrano lo stato "fascicolo vuoto" per un istante, prima dei dati.
   * Per l'utente anonimo i dati sono sincroni (localStorage), quindi parte da true.
   */
  private loadedSubject = new BehaviorSubject<boolean>(true);
  readonly loaded$ = this.loadedSubject.asObservable();

  idFascicolo: number | null = null;

  constructor() {
    this.loadAnon();
    window.addEventListener('storage', (event) => {
      if (event.key === this.ANON_KEY && !this.authService.getCurrentUser()) {
        try {
          const updated = event.newValue ? (JSON.parse(event.newValue) as FascicoloCheckPage[]) : [];
          this.pagineSubject.next(updated);
        } catch {}
      }
    });
  }

  get pagine(): FascicoloCheckPage[] {
    return this.pagineSubject.getValue();
  }

  isInFascicolo(idPagina: number): boolean {
    return this.pagine.some((p) => p.idPagina === idPagina);
  }

  loadFromApi(): void {
    this.loadedSubject.next(false);
    this.fascicoloService.checkFascicolo().subscribe({
      next: (response) => {
        this.idFascicolo = response.data.idFascicolo || null;
        const pagine = (response.data.pagFascicolo || []).sort((a, b) => a.posizione - b.posizione);
        this.pagineSubject.next(pagine);
        localStorage.removeItem(this.ANON_KEY);
        this.loadedSubject.next(true);
      },
      error: () => {
        this.pagineSubject.next([]);
        this.loadedSubject.next(true);
      },
    });
  }

  addPage(page: FascicoloCheckPage): Observable<'added' | 'duplicate'> {
    if (this.isInFascicolo(page.idPagina)) {
      return of('duplicate');
    }

    const user = this.authService.getCurrentUser();
    if (user) {
      return this.fascicoloService
        .addToFascicolo({ idPagina: page.idPagina })
        .pipe(
          tap((res) => {
            if (res.data?.idFascicolo) {
              this.idFascicolo = res.data.idFascicolo;
            }
            this.pagineSubject.next([...this.pagine, page]);
          }),
          map(() => 'added' as const),
        );
    } else {
      const updated = [...this.pagine, page];
      localStorage.setItem(this.ANON_KEY, JSON.stringify(updated));
      this.pagineSubject.next(updated);
      return of('added');
    }
  }

  removePage(idPagina: number): Observable<void> {
    const user = this.authService.getCurrentUser();
    if (user && this.idFascicolo) {
      return this.fascicoloService
        .deleteFromFascicolo({ idFascicolo: this.idFascicolo, idPagina })
        .pipe(
          tap(() =>
            this.pagineSubject.next(
              this.pagine.filter((p) => p.idPagina !== idPagina),
            ),
          ),
          map(() => undefined),
        );
    } else if (!user) {
      const updated = this.pagine.filter((p) => p.idPagina !== idPagina);
      localStorage.setItem(this.ANON_KEY, JSON.stringify(updated));
      this.pagineSubject.next(updated);
    }
    return of(undefined);
  }

  clearAll(): Observable<void> {
    const user = this.authService.getCurrentUser();
    if (user && this.idFascicolo) {
      return this.fascicoloService
        .deleteFromFascicolo({ idFascicolo: this.idFascicolo })
        .pipe(
          tap(() => {
            this.idFascicolo = null;
            this.pagineSubject.next([]);
          }),
          map(() => undefined),
        );
    } else {
      localStorage.removeItem(this.ANON_KEY);
      this.pagineSubject.next([]);
      return of(undefined);
    }
  }

  loadAnon(): void {
    try {
      const saved = localStorage.getItem(this.ANON_KEY);
      this.pagineSubject.next(saved ? (JSON.parse(saved) as FascicoloCheckPage[]) : []);
    } catch {
      this.pagineSubject.next([]);
    }
  }
}
